import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search, Save, X, BookOpen, GraduationCap, Plus, Trash2, Award
} from "lucide-react";

import {
    fetchFormationsRequest,
} from "../../../../redux/actions/formationsActions";

import { fetchSkillsRequest } from "../../../../redux/actions/skillsActions";
import { 
    fetchFormationSkillsRequest, 
    updateFormationSkillsBulkRequest,
    clearFormationSkillError
} from "../../../../redux/actions/formationSkillActions";

import "../RessourcesStyles/FormationManagement.css";
import "../RessourcesStyles/FormationSkillManagement.css";

const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;
    return (
        <div className="fsk-modal-overlay" onMouseDown={onClose}>
            <div className="fsk-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="fsk-modal-header">
                    <h3>
                        <Award size={18} />
                        {title}
                    </h3>
                    <button className="fsk-icon-btn" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="fsk-modal-body">{children}</div>
                {footer ? <div className="fsk-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
};

const FormationSkillManagement = () => {
    const dispatch = useDispatch();
    const { list: formationsList, loading: formationsLoading, error: formationsError } = useSelector((state) => state.formations || {});
    const { list: skillsList, loading: skillsLoading } = useSelector((state) => state.skills || {});
    const { list: currentSkills, loading: fsLoading, error: fsError } = useSelector((state) => state.formationSkill || {});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [associatedSkills, setAssociatedSkills] = useState([]);
    const [q, setQ] = useState("");

    useEffect(() => {
        dispatch(fetchFormationsRequest());
        dispatch(fetchSkillsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (formationsError || fsError) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: formationsError || fsError || "Une erreur est survenue",
                confirmButtonColor: "#2f4595"
            });
            if (fsError) dispatch(clearFormationSkillError());
        }
    }, [formationsError, fsError, dispatch]);

    // Sync modal state with Redux list when data arrives
    useEffect(() => {
        if (isModalOpen && !fsLoading && currentSkills) {
            // Ensure percentages are numbers
            const sanitized = currentSkills.map(s => ({
                ...s,
                pourcentage: Number(s.pourcentage) || 0
            }));
            setAssociatedSkills(sanitized);
        }
    }, [currentSkills, fsLoading, isModalOpen]);

    const formations = useMemo(() => (Array.isArray(formationsList) ? formationsList : []), [formationsList]);
    const skills = useMemo(() => (Array.isArray(skillsList) ? skillsList : []), [skillsList]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        if (!t) return formations;
        return formations.filter((f) =>
            `${f.reference} ${f.nom} ${f.domaineNom} ${f.themeNom}`.toLowerCase().includes(t)
        );
    }, [formations, q]);

    const openManagement = (f) => {
        setSelectedFormation(f);
        setAssociatedSkills([]); // Clear while loading
        dispatch(fetchFormationSkillsRequest(f.id));
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedFormation(null);
        setAssociatedSkills([]);
    };

    const addSkillRow = () => {
        setAssociatedSkills([...associatedSkills, { skillId: "", pourcentage: 0 }]);
    };

    const removeSkillRow = (index) => {
        const newSkills = [...associatedSkills];
        newSkills.splice(index, 1);
        setAssociatedSkills(newSkills);
    };

    const handleSkillChange = (index, skillId) => {
        const newSkills = [...associatedSkills];
        const skill = skills.find(s => s.id === Number(skillId));
        newSkills[index] = { 
            ...newSkills[index], 
            skillId: Number(skillId),
            skillNom: skill ? skill.nom : "",
            skillCode: skill ? skill.code : ""
        };
        setAssociatedSkills(newSkills);
    };

    const handlePercentageChange = (index, value) => {
        const newSkills = [...associatedSkills];
        newSkills[index] = { ...newSkills[index], pourcentage: Number(value) };
        setAssociatedSkills(newSkills);
    };

    const totalPercentage = useMemo(() => {
        return associatedSkills.reduce((sum, s) => sum + (Number(s.pourcentage) || 0), 0);
    }, [associatedSkills]);

    const submit = () => {
        // Recalculate total directly to avoid any stale memo issues
        const total = associatedSkills.reduce((sum, s) => sum + (Number(s.pourcentage) || 0), 0);

        if (total > 100) {
            Swal.fire({
                icon: "warning",
                title: "Attention",
                text: "Le total des pourcentages ne doit pas dépasser 100%.",
                confirmButtonColor: "#2f4595"
            });
            return;
        }

        // Check for duplicates
        const skillIds = associatedSkills.filter(s => s.skillId).map(s => s.skillId);
        const hasDuplicates = new Set(skillIds).size !== skillIds.length;
        if (hasDuplicates) {
            Swal.fire({
                icon: "warning",
                title: "Attention",
                text: "Une compétence ne peut être liée qu'une seule fois.",
                confirmButtonColor: "#2f4595"
            });
            return;
        }

        // Filter out empty rows
        const validSkills = associatedSkills.filter(s => s.skillId);

        const payload = validSkills.map(s => ({
            skillId: s.skillId,
            pourcentage: Number(s.pourcentage) || 0
        }));

        dispatch(updateFormationSkillsBulkRequest(selectedFormation.id, payload));
        closeModal();
    };

    return (
        <div className="fsk-page">
            <div className="fsk-header">
                <div>
                    <h1>Association des compétences aux formations</h1>
                    <p>Définissez la couverture des compétences pour chaque formation</p>
                </div>
            </div>

            <div className="fsk-toolbar">
                <div className="fsk-search-wrap">
                    <Search size={15} className="fsk-search-icon" />
                    <input
                        type="text"
                        className="fsk-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher une formation..."
                    />
                </div>
                <div className="fsk-meta">
                    {formationsLoading ? "Chargement..." : `${filtered.length} formation(s)`}
                </div>
            </div>

            <div className="fsk-card">
                <div className="fsk-table-wrap">
                    <table className="fsk-table">
                        <thead>
                            <tr>
                                <th>Formation</th>
                                <th>Compétences Liées</th>
                                <th>Couverture Totale</th>
                                <th className="fsk-is-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="fsk-empty-cell">
                                        Aucun résultat
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((f) => {
                                    const sum = (f.formationSkills || []).reduce((acc, s) => acc + (s.pourcentage || 0), 0);
                                    return (
                                        <tr key={f.id}>
                                            <td>
                                                <div className="fsk-strong">{f.reference}</div>
                                                <div className="fsk-muted">{f.nom}</div>
                                            </td>
                                            <td>
                                                <div className="fsk-badges">
                                                    {(f.formationSkills || []).length > 0 ? (
                                                        f.formationSkills.map((fs, idx) => (
                                                            <div key={idx} className="fsk-badge" title={`${fs.skillNom} (${fs.pourcentage}%)`}>
                                                                {fs.skillNom}: {fs.pourcentage}%
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="fsk-muted">Aucune compétence</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="fsk-progress-row">
                                                    <div className="fsk-progress">
                                                        <div
                                                            className={`fsk-progress-fill ${sum > 80 ? "is-high" : sum > 40 ? "is-mid" : "is-low"}`}
                                                            style={{ width: `${sum}%` }}
                                                        />
                                                    </div>
                                                    <span className="fsk-strong">{sum}%</span>
                                                </div>
                                            </td>
                                            <td className="fsk-is-right">
                                                <button className="fsk-btn fsk-btn--primary fsk-btn--sm" onClick={() => openManagement(f)}>
                                                    Gérer les liaisons
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={isModalOpen}
                title={`Liaison des compétences : ${selectedFormation?.reference}`}
                onClose={closeModal}
                footer={
                    <>
                        <div className={`fsk-total ${totalPercentage > 100 ? "is-over" : ""}`}>
                            Total couverture : {totalPercentage}% / 100%
                        </div>
                        <button className="fsk-btn fsk-btn--ghost" onClick={closeModal}>Annuler</button>
                        <button className="fsk-btn fsk-btn--primary" onClick={submit}>
                            <Save size={16} /> Enregistrer
                        </button>
                    </>
                }
            >
                <div className="fs-skills-list">
                    <div className="fsk-modal-toolbar">
                        <div className="fsk-help-text">Ajoutez les compétences couvertes par cette formation.</div>
                        <button className="fsk-btn fsk-btn--primary fsk-btn--sm" onClick={addSkillRow}>
                            <Plus size={14} /> Ajouter une compétence
                        </button>
                    </div>

                    {associatedSkills.length === 0 ? (
                        <div className="fsk-empty-box">
                            Cliquez sur le bouton pour ajouter une première compétence.
                        </div>
                    ) : (
                        <div className="fsk-skill-rows">
                            {associatedSkills.map((row, idx) => (
                                <div key={idx} className="fsk-skill-row">
                                    <div className="fsk-skill-select">
                                        <select 
                                            value={row.skillId} 
                                            onChange={(e) => handleSkillChange(idx, e.target.value)}
                                            className="fsk-select"
                                        >
                                            <option value="">-- Sélectionner une compétence --</option>
                                            {skills.map(s => (
                                                <option key={s.id} value={s.id} disabled={associatedSkills.some((as, i) => i !== idx && as.skillId === s.id)}>
                                                    {s.nom} ({s.code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="fsk-percent">
                                        <input 
                                            type="number" 
                                            min="0" 
                                            max="100" 
                                            value={row.pourcentage} 
                                            onChange={(e) => handlePercentageChange(idx, e.target.value)}
                                            className="fsk-input"
                                        />
                                        <span>%</span>
                                    </div>
                                    <button className="fsk-icon-btn fsk-icon-btn--danger" onClick={() => removeSkillRow(idx)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default FormationSkillManagement;
